import { useCallback, useState } from 'react'
import { useHttpsCallable } from 'src/utils/firebase'
import { capture } from 'src/utils/sentry'

type AskPayload = {
  question: string
  /** 直近のやり取り。LLM に文脈として渡す */
  history: { role: 'user' | 'assistant'; content: string }[]
  /** ログイン済みなら案内先を変えるため、クライアント選択済みかだけを渡す（IDは渡さない） */
  isLoggedIn: boolean
}

type AskResponse = {
  answer: string
  /** 回答を出せなかった場合は true。問い合わせへの誘導に使う */
  unanswered: boolean
}

/**
 * 選択式で解決しなかった自由入力を LLM に投げる。
 * 失敗時は throw せず、unanswered 相当のメッセージを返して問い合わせに誘導する。
 */
export const useChatbot = () => {
  const [call] = useHttpsCallable<AskPayload, AskResponse>('askChatbot')
  const [isAsking, setIsAsking] = useState(false)

  const ask = useCallback(
    async (payload: AskPayload): Promise<AskResponse> => {
      setIsAsking(true)
      try {
        const res = await call(payload)
        if (!res?.data?.answer) {
          return { answer: FALLBACK_ANSWER, unanswered: true }
        }
        return res.data
      } catch (e) {
        capture(e)
        return { answer: FALLBACK_ANSWER, unanswered: true }
      } finally {
        setIsAsking(false)
      }
    },
    [call],
  )

  return { ask, isAsking }
}

export const FALLBACK_ANSWER = 'うまくお答えできませんでした。恐れ入りますが、お問い合わせからご連絡ください。担当者が確認してご返信します。'
